import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { AccordionMultiSelectItem, NakedButton, Text } from '@frontend/design-system';
import { formatTime } from '../../utils/utilities';
import { RuleExceptionsFilters } from './use-rule-exceptions-filters';

type FilterItem = {
  type: string;
  value: string;
  label: string;
};

export interface RuleExceptionsAppliedFiltersProps {
  values: RuleExceptionsFilters;
  onRemove: (type: string, value: string) => void;
  filterLists: {
    providers: AccordionMultiSelectItem[];
    services: AccordionMultiSelectItem[];
    operatories: AccordionMultiSelectItem[];
  };
}

const buildFilterItems = (
  values: RuleExceptionsFilters,
  filterLists: RuleExceptionsAppliedFiltersProps['filterLists'],
  t: (key: string) => string
): FilterItem[] => {
  const items: FilterItem[] = [];

  (values.providerIds ?? []).forEach((id) => {
    const label = filterLists.providers.find((p) => p.value === id)?.label ?? id;
    items.push({ type: 'provider', value: id, label });
  });

  (values.resourceIds ?? []).forEach((id) => {
    const label = filterLists.operatories.find((r) => r.value === id)?.label ?? id;
    items.push({ type: 'operatory', value: id, label });
  });

  (values.serviceIds ?? []).forEach((id) => {
    const label = filterLists.services.find((s) => s.value === id)?.label ?? id;
    items.push({ type: 'service', value: id, label });
  });

  if (values.startTime) {
    items.push({ type: 'startTime', value: values.startTime, label: `From: ${formatTime(values.startTime)}` });
  }
  if (values.endTime) {
    items.push({ type: 'endTime', value: values.endTime, label: `To: ${formatTime(values.endTime)}` });
  }
  if (values.startDate) {
    items.push({ type: 'startDate', value: values.startDate, label: `Start: ${values.startDate}` });
  }
  if (values.endDate) {
    items.push({ type: 'endDate', value: values.endDate, label: `End: ${values.endDate}` });
  }
  if (values.status && values.status !== 'current') {
    const statusLabels: Record<string, string> = {
      expired: t('Expired Exceptions'),
    };
    items.push({ type: 'status', value: values.status, label: statusLabels[values.status] ?? values.status });
  }
  if (values.recurrence) {
    const recurrenceLabels = {
      WEEKLY: t('Weekly'),
      SPECIFIC_DATE: t('Specific Date'),
    };
    items.push({
      type: 'recurrence',
      value: values.recurrence,
      label: recurrenceLabels[values.recurrence] ?? values.recurrence,
    });
  }

  return items;
};

export const removeFilter = (values: RuleExceptionsFilters, type: string, value: string): RuleExceptionsFilters => {
  const updated = { ...values };
  switch (type) {
    case 'provider':
      updated.providerIds = updated.providerIds.filter((provider) => provider === value);
      break;
    case 'operatory':
      updated.resourceIds = updated.resourceIds.filter((operatory) => operatory === value);
      break;
    case 'service':
      updated.serviceIds = updated.serviceIds.filter((service) => service === value);
      break;
    case 'startTime':
      updated.startTime = '';
      break;
    case 'endTime':
      updated.endTime = '';
      break;
    case 'startDate':
      updated.startDate = '';
      break;
    case 'endDate':
      updated.endDate = '';
      break;
    case 'status':
      updated.status = 'current';
      break;
    case 'recurrence':
      updated.recurrence = '';
      break;
  }
  return updated;
};

export const RuleExceptionsAppliedFilters = ({ values, onRemove, filterLists }: RuleExceptionsAppliedFiltersProps) => {
  const { t } = useTranslation('schedule-settings');

  const filterItems = buildFilterItems(values, filterLists, t);

  if (!filterItems.length) return null;

  return (
    <div css={containerStyles}>
      <Text size='small' weight='bold'>
        {t('Applied Filters ({{count}})', { count: filterItems.length })}
      </Text>
      <div css={chipsWrapStyles}>
        {filterItems.map((item) => (
          <div key={`${item.type}-${item.value}`} css={chipStyles}>
            <Text size='small' color='default'>
              {item.label}
            </Text>
            <NakedButton
              css={removeButtonStyles}
              onClick={() => onRemove(item.type, item.value)}
              aria-label={t('Remove filter: {{label}}', { label: item.label })}
            >
              <Icon name='x-tiny' color='default' />
            </NakedButton>
          </div>
        ))}
      </div>
    </div>
  );
};

const containerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  backgroundColor: theme.colors.white,
  borderTop: `1px solid ${theme.colors.neutral20}`,
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  padding: `${theme.spacing(2)} 0`,
  maxHeight: 170,
  overflowY: 'auto',
});

const chipsWrapStyles = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
});

const chipStyles = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.borderRadius.small,
  backgroundColor: theme.colors.primary5,
});

const removeButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
});
